/**
 * Delete from the action bar: today's bulk delete (`bulkDeleteMixed`, the
 * chunked `/collections/bulk-delete` + `/artifacts/bulk-delete` calls) and its
 * "Moved to trash" toast whose Undo restores through `/trash/bulk-restore`.
 * Undo is offered only on a plan with `trashRestore`: elsewhere that route
 * answers 402, so the toast says what happened and offers nothing to fail.
 * Same flow, copy and outcomes as the collection page's bulk delete
 * (ProjectDetail), aimed at workspace rows instead of hierarchy children.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { WorkspaceItem } from '@bower/shared';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { useCanUseFeature } from '../../../hooks/ui/useFeatureGate';
import { apiService } from '../../../services/api.service';
import { bulkRestoreTrashItems } from '../../../services/api/workspace-admin.api';
import { safeAnalytics } from '../../../utils/analytics';
import { bulkDeleteMixed } from '../../../utils/bulkOperations';
import { ActionToast } from '../../ui/ActionToast';
import { refreshFolderLists } from '../../../lib/folderListRefresh';

const typeOf = (item: WorkspaceItem) => (item.kind === 'folder' ? 'project' : 'artifact');

export function useBulkTrash() {
  const workspaceId = useWorkspaceId() ?? '';
  const queryClient = useQueryClient();
  const toast = useAppToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const canRestore = useCanUseFeature('trashRestore', { track: false }).allowed;

  const refresh = useCallback(
    () => refreshFolderLists(queryClient, { queryKey: ['workspace-items', { workspaceId }] }),
    [queryClient, workspaceId],
  );

  const undo = useCallback(
    async (items: ReadonlyArray<WorkspaceItem>) => {
      const noun = items.length === 1 ? 'item' : 'items';
      try {
        const restore = await bulkRestoreTrashItems(
          apiService,
          items.map((item) => ({ id: item.id, type: typeOf(item) })),
        );
        if (restore.failed === 0)
          toast({ title: `${items.length} ${noun} restored`, status: 'success' });
        else if (restore.succeeded === 0) toast({ title: 'Failed to undo', status: 'error' });
        else
          toast({
            title: `Restored ${restore.succeeded} of ${items.length} ${noun}. ${restore.failed} failed.`,
            status: 'warning',
          });
      } catch {
        toast({ title: 'Failed to undo', status: 'error' });
      }
      void refresh();
    },
    [refresh, toast],
  );

  /** Resolves with the items that reached the trash (none on a failure). */
  const trash = useCallback(
    async (items: ReadonlyArray<WorkspaceItem>): Promise<WorkspaceItem[]> => {
      setIsDeleting(true);
      try {
        const result = await bulkDeleteMixed({
          projects: items.filter((i) => i.kind === 'folder').map((i) => i.id),
          artifacts: items.filter((i) => i.kind === 'file').map((i) => i.id),
        });
        safeAnalytics.track(ANALYTICS_EVENTS.BULK_OPERATION_COMPLETED, {
          operation_type: 'delete',
          entity_type: 'mixed',
          item_count: result.succeeded.length,
          failed_count: result.failed.length,
          total_count: result.total,
        });
        const done = new Set(result.succeeded);
        const trashed = items.filter((item) => done.has(item.id));
        const noun = trashed.length === 1 ? 'item' : 'items';
        const description = `${trashed.length} ${noun} moved to trash. Permanently deleted after 30 days.`;
        if (result.failed.length === 0 && !canRestore) {
          toast({
            title: 'Moved to trash',
            description,
            status: 'info',
            duration: 8000,
            isClosable: true,
          });
        } else if (result.failed.length === 0) {
          toast({
            duration: 8000,
            isClosable: true,
            render: ({ onClose }: { onClose: () => void }) => (
              <ActionToast
                title="Moved to trash"
                description={description}
                status="info"
                actionLabel="Undo"
                onAction={() => void undo(trashed)}
                onClose={onClose}
              />
            ),
          });
        } else {
          toast({
            title: trashed.length === 0 ? 'Could not move to trash' : 'Partially moved to trash',
            description:
              trashed.length === 0
                ? `${result.failed.length} of ${result.total} items could not be moved to trash.`
                : `Moved ${trashed.length} of ${result.total} items to trash. ${result.failed.length} failed.`,
            status: trashed.length === 0 ? 'error' : 'warning',
            duration: 7000,
          });
        }
        return trashed;
      } catch (error) {
        toast({ title: 'Error', description: (error as Error).message, status: 'error' });
        return [];
      } finally {
        setIsDeleting(false);
        void refresh();
      }
    },
    [canRestore, refresh, toast, undo],
  );

  return { trash, isDeleting };
}
